import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { RagBadge } from "@/components/common/RagBadge";
import { fmtDay, fmtDateTime } from "@/lib/format";
import { fmtDur, fmtTime } from "@/lib/people";

function PersonDialog({ p, tz, onClose }) {
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-h-[88vh] max-w-xl overflow-y-auto" data-testid="person-time-dialog">
        <DialogHeader>
          <DialogTitle>{p.name} · time records</DialogTitle>
          <DialogDescription>Worked time only. Corrected records keep their original times in the history below each entry.</DialogDescription>
        </DialogHeader>
        {p.entries.length === 0 ? <p className="py-6 text-center text-sm text-[#1E2D44]/45">No time recorded this week.</p> : (
          <div className="space-y-2">
            {p.entries.map((e) => (
              <div key={e.id} className="rounded-lg border border-[#EEF1F6] px-3 py-2.5" data-testid={`person-entry-${e.id}`}>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="w-24 text-[#1E2D44]/60">{fmtDay(e.local_date)}</span>
                  <span className="font-medium text-[#0B1729]">{fmtTime(e.clock_in, tz)} – {e.clock_out ? fmtTime(e.clock_out, tz) : "still clocked in"}</span>
                  <span className="ml-auto font-semibold">{fmtDur(e.minutes)}</span>
                  {e.corrected && <RagBadge rag="blue" dot={false} className="!py-0 !text-[10px]">Corrected</RagBadge>}
                </div>
                {(e.history || []).map((h, i) => (
                  <div key={i} className="mt-1.5 rounded bg-[#F7F9FC] px-2 py-1 text-[11px] text-[#1E2D44]/60">
                    {h.before ? `Was ${fmtTime(h.before.clock_in, tz)} – ${h.before.clock_out ? fmtTime(h.before.clock_out, tz) : "open"}` : "Added from a correction"} · approved by {h.by_name} {fmtDateTime(h.at)} · “{h.reason}”
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function TeamTimeTab({ orgId }) {
  const [week, setWeek] = useState(null);
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    setD(null);
    api.get(`/workspace/clock/team?org_id=${orgId}${week ? `&week_start=${week}` : ""}`).then((r) => setD(r.data));
  }, [orgId, week]);
  if (!d) return <Icons.Loader2 className="mx-auto mt-8 animate-spin text-[#FFB000]" />;
  const shift = (n) => setWeek(dayjs(d.week_start).add(n * 7, "day").format("YYYY-MM-DD"));

  return (
    <div data-testid="team-time-tab">
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => shift(-1)} data-testid="time-prev-week"><Icons.ChevronLeft size={16} /></Button>
        <div className="min-w-[180px] text-center font-display text-sm font-bold text-[#0B1729]" data-testid="time-week-label">
          Week of {dayjs(d.week_start).format("D MMM YYYY")}
        </div>
        <Button size="icon" variant="outline" className="h-8 w-8" onClick={() => shift(1)} data-testid="time-next-week"><Icons.ChevronRight size={16} /></Button>
        <span className="ml-auto text-xs text-[#1E2D44]/50">Worked time only · times in {d.timezone}</span>
      </div>
      <div className="amden-card overflow-x-auto p-0">
        <table className="w-full min-w-[760px] text-sm" data-testid="team-time-table">
          <thead><tr className="border-b border-[#E4E8EF] text-left text-xs uppercase tracking-wide text-[#1E2D44]/45">
            <th className="px-4 py-3 font-semibold">Person</th>
            {d.days.map((x) => <th key={x} className={`px-2 py-3 text-center font-semibold ${x === d.today ? "text-[#FFB000]" : ""}`}>{dayjs(x).format("ddd D")}</th>)}
            <th className="px-4 py-3 text-right font-semibold">Week</th>
          </tr></thead>
          <tbody>
            {d.people.map((p) => (
              <tr key={p.user_id} onClick={() => setOpen(p)} className="cursor-pointer border-b border-[#EEF1F6] hover:bg-[#F7F9FC]" data-testid={`time-row-${p.user_id}`}>
                <td className="px-4 py-2.5">
                  <div className="font-medium text-[#0B1729]">{p.name}</div>
                  {p.clocked_in_since
                    ? <div className="flex items-center gap-1 text-[11px] text-[#3D8B65]"><span className="h-1.5 w-1.5 rounded-full bg-[#3D8B65]" /> In since {fmtTime(p.clocked_in_since, d.timezone)}</div>
                    : <div className="text-[11px] text-[#1E2D44]/40">{p.title}</div>}
                </td>
                {d.days.map((x) => (
                  <td key={x} className="px-2 py-2.5 text-center">
                    <div className={p.days[x] ? "font-semibold text-[#0B1729]" : "text-[#1E2D44]/25"}>{p.days[x] ? fmtDur(p.days[x]) : "—"}</div>
                    {p.scheduled[x] > 0 && <div className="text-[10px] text-[#1E2D44]/40">of {p.scheduled[x]}h</div>}
                  </td>
                ))}
                <td className="px-4 py-2.5 text-right"><div className="font-semibold text-[#0B1729]">{fmtDur(p.total_minutes)}</div><div className="text-[10px] text-[#1E2D44]/40">scheduled {p.scheduled_hours}h</div></td>
              </tr>
            ))}
            {d.people.length === 0 && <tr><td colSpan={9} className="p-6 text-center text-sm text-[#1E2D44]/45">No one with an active employee profile in your team.</td></tr>}
          </tbody>
        </table>
      </div>
      {open && <PersonDialog p={open} tz={d.timezone} onClose={() => setOpen(null)} />}
    </div>
  );
}
